import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { instructions } from '@/impactReports/impactReport.types';
import { useAI } from '@/impactReports/useAI.hook';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { faPaperPlane, faSparkles } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Alert, Button, LoadingIndicator, TextArea } from '@seeqdev/qomponents';

interface ImpactReportAIOverlayIF {
  updateCKContent: (description: string) => void;
  onClose: () => void;
  workbookId: string | undefined;
  worksheetId: string | undefined;
}

export const ImpactReportAIOverlay = ({
  updateCKContent,
  onClose,
  workbookId,
  worksheetId,
}: ImpactReportAIOverlayIF) => {
  const { t } = useTranslation();
  const [spinning, setSpinning] = useState(false);
  const [displayCustomPrompt, setDisplayCustomPrompt] = useState(false);

  const { getImpactReportText, abortImpactReportTextQuery } = useAI();

  const [customPrompt, setCustomPrompt] = useState('');
  const generateImpactReportDescription = async () => {
    if (!workbookId || !worksheetId) return;
    const aiText = await getImpactReportText(workbookId, worksheetId, customPrompt);
    aiText && updateCKContent(aiText);
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const textArray = _.range(1, 10).map((val) => t(`IMPACT_REPORT.LOADING_PROMPT_${val}`));
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1 < textArray.length ? prevIndex + 1 : prevIndex));
    }, 8000);

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [textArray.length]);

  const closeAndAbort = () => {
    abortImpactReportTextQuery();
    doTrack(TRACK.AI_ASSISTANT, 'AI content generation for Impact Report aborted');
    setCurrentIndex(0);
    onClose();
  };

  return (
    <Alert dismissible={true} variant="gray" xstyle={styles.overlay} onClose={closeAndAbort}>
      {spinning ? (
        <div {...stylex.props(styles.loading)}>
          <span {...stylex.props(styles.spinner)}>
            <LoadingIndicator size="md" />
          </span>
          <p {...stylex.props(styles.loadingText)}>{textArray[currentIndex]}</p>
        </div>
      ) : (
        <div {...stylex.props(styles.content)}>
          {displayCustomPrompt ? (
            <div {...stylex.props(styles.column)}>
              <div {...stylex.props(styles.promptTitle)}>{t('IMPACT_REPORT.USE_CUSTOM_PROMPT')}</div>
              <div {...stylex.props(styles.promptRow)}>
                <TextArea
                  onChange={(event) => {
                    setCustomPrompt(event.currentTarget.value);
                  }}
                  value={customPrompt}
                  xstyle={styles.prompt}
                  placeholder={`Enter your prompt here. You can get started with something like:"${t(
                    'IMPACT_REPORT.GENERATE_DESCRIPTION_PROMPT',
                  )}"`}
                />
                <div {...stylex.props(styles.submit)}>
                  <Button
                    variant="theme"
                    onClick={async () => {
                      setSpinning(true);
                      await generateImpactReportDescription();
                      setSpinning(false);
                      setCurrentIndex(0);
                      doTrack(TRACK.AI_ASSISTANT, 'Impact Report generated with custom prompt');
                      onClose();
                    }}
                    size="sm"
                    testId="submitPrompt"
                    xstyle={styles.submitButton}
                    extraClassNames="ai-button"
                    icon={faPaperPlane}
                    iconStyle="white"
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <span {...stylex.props(styles.quickStart)}>{t('JOURNAL.QUICK_START')}</span>
              <Button
                label={t('IMPACT_REPORT.FILL_WITH_AI')}
                icon={faSparkles}
                onClick={async () => {
                  setSpinning(true);
                  await generateImpactReportDescription();
                  setSpinning(false);
                  setCurrentIndex(0);
                  doTrack(TRACK.AI_ASSISTANT, 'Impact Report generated with default prompt');
                  onClose();
                }}
                variant="theme"
              />
              <Button
                label={t('IMPACT_REPORT.USE_CUSTOM_PROMPT')}
                icon={faSparkles}
                xstyle={styles.customPromptButton}
                onClick={() => {
                  setDisplayCustomPrompt(true);
                }}
                variant="theme"
              />
              <Button
                label={t('IMPACT_REPORT.FILL_WITH_TEMPLATE')}
                onClick={() => {
                  updateCKContent(instructions);
                  doTrack(TRACK.AI_ASSISTANT, 'Impact Report generated with default template - not AI generated');
                  onClose();
                }}
                variant="theme"
                testId="fillWithTemplate"
              />
            </>
          )}
        </div>
      )}
    </Alert>
  );
};

const pulse = stylex.keyframes({ '0%': { opacity: 1 }, '50%': { opacity: 0.8 }, '100%': { opacity: 1 } });
const styles = stylex.create({
  loading: {
    display: 'flex',
    flexDirection: 'column',
    height: 200,
    maxHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: { paddingBottom: 15 },
  loadingText: {
    textAlign: 'center',
    animationName: pulse,
    animationDuration: '1.5s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    transitionProperty: 'opacity',
    transitionDuration: '0.5s',
    transitionTimingFunction: 'ease-in-out',
  },
  content: { display: 'flex', flexDirection: 'column', height: 200, maxHeight: 200, alignItems: 'center' },
  column: { display: 'flex', flexDirection: 'column' },
  promptTitle: { fontSize: 'larger', marginBottom: 5, marginLeft: 10 },
  promptRow: { display: 'flex', flexDirection: 'row' },
  submit: { marginTop: 'auto', marginLeft: 5 },
  quickStart: { fontSize: 'large', marginBottom: 15 },
  overlay: { width: '60%', height: 200, maxHeight: 200, padding: 20 },
  prompt: { width: 300 },
  submitButton: { paddingLeft: 8 },
  customPromptButton: { margin: 20 },
});
